/**
 * WOOF Pet Form
 * Custom element powering the combined per-pet personalization block: one
 * card per necklace, each grouping a photo dropzone, a pet-name toggle and an
 * engraving toggle — so every pet can have its own name/engraving choice.
 * - cards follow the pendant count selected in WOOF Quantity Breaks; the
 *   necklace itself remains quantity one
 * - click / drag & drop file selection with live preview per card
 * - client-side max file size validation
 * - HEIC/HEIF photos are converted to browser-compatible JPEG before preview
 *   and upload
 * - each photo is renamed to a ULID (e.g. 01KWYF4EMST7A6MXGS9FY313KP.jpg) and
 *   POSTed to the upload service; the retrieval URL and the ULID filename are
 *   attached as numbered line item properties ("Pet photo 1", "_Photo ID 1",
 *   …), which ties every file to its product line and, once purchased, to the
 *   Shopify order
 * - name/engraving values submit as numbered properties ("Pet's name 1",
 *   "Engraving on the back 1", …) only while their toggle is on; a toggled-on empty field
 *   blocks Add to cart with an inline error
 * - extra pendants and enabled engravings add their configured paid variants
 *   as nested child lines of the necklace
 * - the upload endpoint sends no CORS headers, so the POST runs in no-cors
 *   mode and success is confirmed by loading the retrieval URL as an image
 * - Add to cart is held back while any upload is still running
 * - photo guide <dialog> open/close
 */

import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { prepareUploadImage } from './WOOF-image-upload.js';

const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const BEFORE_PRODUCT_FORM_SUBMIT_EVENT = 'woof:before-product-form-submit';

function generateUlid() {
  let timestamp = Date.now();
  let time = '';
  for (let i = 0; i < 10; i++) {
    time = ULID_ALPHABET[timestamp % 32] + time;
    timestamp = Math.floor(timestamp / 32);
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let random = '';
  for (let i = 0; i < 16; i++) random += ULID_ALPHABET[bytes[i] % 32];
  return time + random;
}

const EXTENSION_BY_TYPE = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/avif': 'avif',
};

function fileExtension(file) {
  const parts = file.name.split('.');
  if (parts.length > 1) {
    const fromName = parts.pop().toLowerCase();
    if (/^[a-z0-9]{2,5}$/.test(fromName)) return fromName;
  }
  return EXTENSION_BY_TYPE[file.type] || 'jpg';
}

/** Resolves when the URL loads as an image; images bypass CORS, so this also
 *  serves as the upload success check. Retries to ride out propagation lag. */
function verifyImage(url, attempts = 3) {
  return new Promise((resolve, reject) => {
    const attempt = (remaining) => {
      const probe = new Image();
      probe.onload = () => resolve();
      probe.onerror = () => {
        if (remaining <= 1) reject(new Error('Uploaded file not retrievable'));
        else setTimeout(() => attempt(remaining - 1), 800);
      };
      probe.src = `${url}&_probe=${Date.now()}`;
    };
    attempt(attempts);
  });
}

class WoofPetForm extends HTMLElement {
  connectedCallback() {
    this.guide = this.querySelector('[data-guide]');
    this.notice = this.querySelector('[data-notice]');
    this.noticeText = this.querySelector('[data-notice-text]');
    this.required = this.dataset.required === 'true';
    this.activeCount = 1;

    this.slots = [...this.querySelectorAll('[data-slot]')].map((el, index) => ({
      el,
      index,
      title: el.querySelector('[data-slot-title]'),
      drop: el.querySelector('[data-drop]'),
      input: el.querySelector('[data-input]'),
      urlInput: el.querySelector('[data-url-input]'),
      idInput: el.querySelector('[data-id-input]'),
      placeholder: el.querySelector('[data-placeholder]'),
      preview: el.querySelector('[data-preview]'),
      previewImg: el.querySelector('[data-preview-img]'),
      fileName: el.querySelector('[data-file-name]'),
      uploadingEl: el.querySelector('[data-uploading]'),
      uploadedEl: el.querySelector('[data-uploaded]'),
      removeBtn: el.querySelector('[data-remove]'),
      errorEl: el.querySelector('[data-error]'),
      titleName: el.querySelector('[data-slot-name]'),
      nameToggle: el.querySelector('[data-name-toggle]'),
      nameField: el.querySelector('[data-name-field]'),
      nameInput: el.querySelector('[data-name-input]'),
      nameCount: el.querySelector('[data-name-count]'),
      engToggle: el.querySelector('[data-eng-toggle]'),
      engField: el.querySelector('[data-eng-field]'),
      engInput: el.querySelector('[data-eng-input]'),
      engCount: el.querySelector('[data-eng-count]'),
      active: index === 0,
      state: null,
      abort: null,
      selectionToken: null,
      previewUrl: null,
    }));

    if (!this.slots.length) return;
    this.slots.forEach((slot) => this.#wireSlot(slot));

    this.form = document.getElementById(this.dataset.formId || '') || this.slots[0].input.form;

    // Horizon asks this component whether it should take over submission before
    // its standard single-product request runs. This prevents the necklace from
    // being added once by Horizon and again by the engraving batch request.
    this.productForm = this.form?.closest('product-form-component');
    this.handleProductFormSubmit = (event) => {
      if (event.detail?.form !== this.form) return;

      const busy = this.#activeSlots().filter((slot) => slot.input.files?.length && slot.state !== 'done');
      if (busy.length) {
        event.preventDefault();
        if (busy.some((slot) => slot.state === 'error')) {
          this.pendingSubmit = false;
          busy
            .filter((slot) => slot.state === 'error')
            .forEach((slot) =>
              this.#showError(slot, this.dataset.errorUpload || 'The upload didn’t work — please try again.')
            );
        } else {
          this.pendingSubmit = true;
        }
        return;
      }

      const engravingCount = this.#activeSlots().filter((slot) => slot.engToggle?.checked).length;
      const extraPendantCount = Math.max(0, this.activeCount - 1);
      if (!engravingCount && !extraPendantCount) return;

      event.preventDefault();
      this.#addWithAddons(extraPendantCount, engravingCount);
    };
    this.productForm?.addEventListener(BEFORE_PRODUCT_FORM_SUBMIT_EVENT, this.handleProductFormSubmit);

    // Follow the pendant count stored by WOOF Quantity Breaks without changing
    // the necklace's actual Shopify quantity.
    this.handlePendantCountChange = (event) => this.#setActiveCount(Number(event.detail?.count) || 1);
    this.form?.addEventListener('woof:pendant-count-change', this.handlePendantCountChange);
    const pendantCountInput = this.form?.elements?.namedItem('properties[_WOOF pendant count]');
    this.#setActiveCount(
      pendantCountInput instanceof HTMLInputElement ? Number(pendantCountInput.value) || 1 : 1
    );

    this.querySelectorAll('[data-guide-open]').forEach((button) =>
      button.addEventListener('click', () => this.guide?.showModal())
    );
    this.querySelectorAll('[data-guide-close]').forEach((button) =>
      button.addEventListener('click', () => this.guide?.close())
    );
    // Close when clicking the backdrop (outside the dialog content)
    this.guide?.addEventListener('click', (event) => {
      const rect = this.guide.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) this.guide.close();
    });
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener(BEFORE_PRODUCT_FORM_SUBMIT_EVENT, this.handleProductFormSubmit);
    this.form?.removeEventListener('woof:pendant-count-change', this.handlePendantCountChange);
  }

  #activeSlots() {
    return this.slots.slice(0, this.activeCount);
  }

  #setActiveCount(quantity) {
    this.activeCount = Math.min(Math.max(quantity, 1), this.slots.length);

    this.slots.forEach((slot, index) => {
      const active = index < this.activeCount;
      slot.active = active;
      slot.el.hidden = !active;
      slot.input.disabled = !active;
      slot.input.required = active && this.required;
      // Only submit properties for slots that are part of the bundle
      const hasUpload = slot.state === 'done';
      if (slot.urlInput) slot.urlInput.disabled = !active || !hasUpload;
      if (slot.idInput) slot.idInput.disabled = !active || !hasUpload;
      this.#applyToggle(slot, slot.nameToggle, slot.nameField, slot.nameInput, false);
      this.#applyToggle(slot, slot.engToggle, slot.engField, slot.engInput);
      this.#syncTitleName(slot);
    });

    if (this.notice) {
      this.notice.hidden = this.activeCount <= 1;
      if (this.noticeText) {
        this.noticeText.textContent = (this.dataset.noticeTemplate || 'Please upload [count] photos.').replace(
          '[count]',
          String(this.activeCount)
        );
      }
    }
  }

  #wireSlot(slot) {
    slot.input.addEventListener('change', () => this.#onFileChange(slot));
    slot.input.addEventListener('invalid', (event) => this.#onInvalid(slot, event));

    slot.removeBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#clear(slot);
    });

    ['dragenter', 'dragover'].forEach((name) =>
      slot.drop.addEventListener(name, (event) => {
        event.preventDefault();
        slot.drop.classList.add('is-dragover');
      })
    );
    ['dragleave', 'drop'].forEach((name) =>
      slot.drop.addEventListener(name, (event) => {
        event.preventDefault();
        slot.drop.classList.remove('is-dragover');
      })
    );
    slot.drop.addEventListener('drop', (event) => {
      const files = event.dataTransfer?.files;
      if (files?.length) {
        slot.input.files = files;
        this.#onFileChange(slot);
      }
    });

    this.#wireOption(slot, slot.nameToggle, slot.nameField, slot.nameInput, slot.nameCount, false);
    this.#wireOption(slot, slot.engToggle, slot.engField, slot.engInput, slot.engCount);

    // Mirror the typed name into the card title ("Pet 1: Maxie")
    slot.nameInput?.addEventListener('input', () => this.#syncTitleName(slot));
    slot.nameToggle?.addEventListener('change', () => this.#syncTitleName(slot));
    this.#syncTitleName(slot);
  }

  #syncTitleName(slot) {
    const on = slot.nameToggle ? slot.nameToggle.checked : true;
    const value = on ? (slot.nameInput?.value || '').trim() : '';
    if (slot.titleName) slot.titleName.textContent = value ? `: ${value}` : '';
    if (slot.title) slot.title.hidden = false;
  }

  /** Shared wiring for the per-pet name / engraving toggles. */
  #wireOption(slot, toggle, field, input, count, required = true) {
    if (!toggle || !field || !input) return;

    toggle.addEventListener('change', () => {
      this.#applyToggle(slot, toggle, field, input, required);
      input.closest('[data-opt]')?.classList.remove('has-error');
      if (toggle.checked && slot.active) input.focus();
    });

    input.addEventListener('input', () => {
      const max = input.getAttribute('maxlength') || '';
      if (count) count.textContent = `${input.value.length}/${max}`;
      if (input.value.trim() !== '') input.closest('[data-opt]')?.classList.remove('has-error');
    });

    input.addEventListener('invalid', (event) => {
      if (!input.validity.valueMissing) return;
      event.preventDefault();
      input.closest('[data-opt]')?.classList.add('has-error');
      input.closest('[data-slot]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.focus({ preventScroll: true });
    });
  }

  #applyToggle(slot, toggle, field, input, required = true) {
    if (!toggle || !field || !input) return;
    const on = slot.active && toggle.checked;
    field.hidden = !toggle.checked;
    input.disabled = !on;
    input.required = on && required;
  }

  /**
   * Adds the necklace and its paid add-ons in one request. Shopify's parent_id
   * field keeps the add-ons nested beneath the configured necklace.
   * @param {number} extraPendantCount
   * @param {number} engravingCount
   */
  async #addWithAddons(extraPendantCount, engravingCount) {
    if (this.addingToCart || !this.form) return;

    const pendantAddonVariantId = Number(this.dataset.pendantAddonVariantId);
    if (extraPendantCount > 0 && !pendantAddonVariantId) {
      this.#showCartError(
        this.dataset.pendantUnavailable || 'Additional pendants are temporarily unavailable. Please try again later.'
      );
      return;
    }

    const engravingAddonVariantId = Number(this.dataset.engravingAddonVariantId);
    if (engravingCount > 0 && !engravingAddonVariantId) {
      this.#showCartError(
        this.dataset.engravingUnavailable || 'Engraving is temporarily unavailable. Please try again later.'
      );
      return;
    }

    const formData = new FormData(this.form);
    const parentVariantId = Number(formData.get('id'));
    // A configured necklace is always one parent line. Pendant count is priced
    // exclusively by the nested €20 add-on, so an older cart quantity must not
    // multiply the base necklace price.
    const quantity = 1;
    if (!parentVariantId) return;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)]$/);
      if (!match || typeof value !== 'string' || value.trim() === '') continue;
      properties[match[1]] = value;
    }
    const engravingPropertyKeys = this.#activeSlots()
      .filter((slot) => slot.engToggle?.checked)
      .map((slot) => slot.engInput?.name.match(/^properties\[(.+)]$/)?.[1])
      .filter(Boolean);

    const parentItem = {
      id: parentVariantId,
      quantity,
      properties,
    };
    const sellingPlan = formData.get('selling_plan');
    if (typeof sellingPlan === 'string' && sellingPlan) parentItem.selling_plan = Number(sellingPlan);

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => component instanceof HTMLElement && component.dataset.sectionId)
      .filter(Boolean);

    const items = [parentItem];
    if (extraPendantCount > 0) {
      items.push({
        id: pendantAddonVariantId,
        quantity: extraPendantCount,
        parent_id: parentVariantId,
        properties: {
          '_WOOF pendant add-on': 'true',
          '_WOOF pendant count': String(this.activeCount),
        },
      });
    }
    if (engravingCount > 0) {
      items.push({
        id: engravingAddonVariantId,
        quantity: engravingCount,
        parent_id: parentVariantId,
        properties: {
          '_WOOF engraving add-on': 'true',
          '_WOOF engraving property keys': engravingPropertyKeys.join('|'),
        },
      });
    }

    const payload = {
      items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    this.addingToCart = true;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        const error = new Error(result.description || result.message || 'Unable to add this item to the cart.');
        error.data = result;
        throw error;
      }

      let cart;
      try {
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (error) {
        console.warn('Engraving was added, but the refreshed cart could not be fetched.', error);
      }

      const productForm = this.form.closest('product-form-component');
      this.#clearCartError();
      this.#announceAdded(productForm);
      (productForm || this).dispatchEvent(
        new CartAddEvent(cart, parentVariantId.toString(), {
          source: 'product-form-component',
          itemCount: quantity + extraPendantCount + engravingCount,
          productId: productForm.dataset.productId,
          variantId: parentVariantId.toString(),
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error(error);
      const data = error.data || {};
      const message = error.message || 'Unable to add this item to the cart.';
      this.#showCartError(message, data);
    } finally {
      this.addingToCart = false;
    }
  }

  #showCartError(message, data = {}) {
    const productForm = this.form?.closest('product-form-component');
    productForm?.dispatchEvent(
      new CartErrorEvent(this.form?.id || '', message, data.description || message, data.errors || {})
    );

    const errorElement = productForm?.querySelector('[ref="addToCartTextError"]');
    if (errorElement) {
      let textNode = errorElement.childNodes[2];
      if (textNode?.nodeType !== Node.TEXT_NODE) textNode = null;
      if (!textNode) {
        textNode = document.createTextNode('');
        errorElement.appendChild(textNode);
      }
      textNode.textContent = message;
      errorElement.classList.remove('hidden');
      errorElement.setAttribute('aria-live', 'assertive');
    }

    const liveRegion = productForm?.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = message;
  }

  #clearCartError() {
    const productForm = this.form?.closest('product-form-component');
    const errorElement = productForm?.querySelector('[ref="addToCartTextError"]');
    errorElement?.classList.add('hidden');
    errorElement?.removeAttribute('aria-live');
  }

  #announceAdded(productForm) {
    const liveRegion = productForm?.querySelector('[ref="liveRegion"]');
    if (!liveRegion) return;
    liveRegion.textContent = Theme.translations.added || 'Added to cart';
    window.setTimeout(() => {
      liveRegion.textContent = '';
    }, 5000);
  }

  async #onFileChange(slot) {
    const file = slot.input.files?.[0];
    this.#hideError(slot);
    if (!file) {
      this.#clear(slot);
      return;
    }

    const maxMb = Number(this.dataset.maxMb || 20);
    if (file.size > maxMb * 1024 * 1024) {
      this.#clear(slot);
      this.#showError(slot, (this.dataset.errorSize || 'File too large ([max] MB max).').replace('[max]', String(maxMb)));
      return;
    }

    slot.abort?.abort();
    const selectionToken = Symbol('photo-selection');
    slot.selectionToken = selectionToken;
    this.#setUploadState(slot, 'uploading');
    slot.fileName.textContent = file.name;
    slot.previewImg.hidden = true;
    slot.drop.hidden = true;
    slot.preview.hidden = false;
    slot.input.setCustomValidity('');

    try {
      const uploadFile = await prepareUploadImage(file);
      if (slot.selectionToken !== selectionToken) return;

      if (slot.previewUrl) URL.revokeObjectURL(slot.previewUrl);
      slot.previewUrl = URL.createObjectURL(uploadFile);
      slot.previewImg.src = slot.previewUrl;
      slot.previewImg.hidden = false;
      this.#startUpload(slot, uploadFile);
    } catch (error) {
      if (slot.selectionToken !== selectionToken) return;
      this.pendingSubmit = false;
      this.#setUploadState(slot, 'error');
      this.#showError(slot, this.dataset.errorUpload || 'The upload didn’t work — please try again.');
    }
  }

  async #startUpload(slot, file) {
    slot.abort?.abort();
    slot.abort = new AbortController();
    const { signal } = slot.abort;

    this.#setUploadState(slot, 'uploading');
    [slot.urlInput, slot.idInput].forEach((input) => {
      if (!input) return;
      input.value = '';
      input.disabled = true;
    });

    try {
      const endpoint = (this.dataset.uploadEndpoint || '').replace(/\/+$/, '');
      if (!endpoint) throw new Error('Upload endpoint not configured');

      const uploadName = `${generateUlid()}.${fileExtension(file)}`;
      const formData = new FormData();
      formData.append('files', file, uploadName);

      // The endpoint sends no CORS headers; a multipart POST is a CORS-simple
      // request, so no-cors delivers it — the opaque response is verified by
      // loading the file back below.
      await fetch(`${endpoint}/api/upload`, { method: 'POST', body: formData, mode: 'no-cors', signal });

      const fileUrl = `${endpoint}/api/files?file=${uploadName}`;
      await verifyImage(fileUrl);
      if (signal.aborted) return;

      if (slot.urlInput) {
        slot.urlInput.value = fileUrl;
        slot.urlInput.disabled = false;
      }
      if (slot.idInput) {
        slot.idInput.value = uploadName;
        slot.idInput.disabled = false;
      }
      this.#setUploadState(slot, 'done');

      if (this.pendingSubmit && this.#activeSlots().every((s) => !s.input.files?.length || s.state === 'done')) {
        this.pendingSubmit = false;
        this.form?.requestSubmit();
      }
    } catch (error) {
      if (signal.aborted) return;
      this.pendingSubmit = false;
      this.#setUploadState(slot, 'error');
      this.#showError(slot, this.dataset.errorUpload || 'The upload didn’t work — please try again.');
    }
  }

  #setUploadState(slot, state) {
    slot.state = state;
    if (slot.uploadingEl) slot.uploadingEl.hidden = state !== 'uploading';
    if (slot.uploadedEl) slot.uploadedEl.hidden = state !== 'done';
  }

  #onInvalid(slot, event) {
    if (!slot.input.validity.valueMissing) return;
    event.preventDefault();
    this.#showError(slot, this.dataset.errorRequired || 'Please upload a photo first.');
    slot.drop.classList.add('has-error');
    slot.drop.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  #clear(slot) {
    slot.abort?.abort();
    slot.selectionToken = null;
    this.pendingSubmit = false;
    slot.input.value = '';
    [slot.urlInput, slot.idInput].forEach((input) => {
      if (!input) return;
      input.value = '';
      input.disabled = true;
    });
    if (slot.previewUrl) {
      URL.revokeObjectURL(slot.previewUrl);
      slot.previewUrl = null;
    }
    slot.previewImg.removeAttribute('src');
    slot.drop.hidden = false;
    slot.preview.hidden = true;
    this.#setUploadState(slot, null);
  }

  #showError(slot, message) {
    if (!slot.errorEl) return;
    slot.errorEl.textContent = message;
    slot.errorEl.classList.add('is-visible');
  }

  #hideError(slot) {
    slot.errorEl?.classList.remove('is-visible');
    slot.drop.classList.remove('has-error');
  }
}

if (!customElements.get('woof-pet-form')) {
  customElements.define('woof-pet-form', WoofPetForm);
}
